import styled from "styled-components";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GameProposalDetailsAPI, LabsGameProposalStartTestAPI, LabsGameStartTestAPI } from "../TestManagementApi";
import { StatusButton } from './components/StatusButton';
import { StatusBox } from './components/StatusBox';
import { ApprovedCheck } from './components/ApprovedCheck';
import { FinalStatusButton } from "./components/FinalStatusButton";
import { useCallback, useState } from 'react';
import CheckTestStartModal from './Modal/CheckTestStartModal';
import instance from "src/API/MainAPI";

interface GameProposalDetailsResponse {
  gameProposalId: number;
  status: string;
  testPlanStatus: string;
  projectInfoStatus: string;
  questionCompositionStatus: string;
  previewAvailable: boolean;
  submitAvailable: boolean;
  gameName?: string;
}

const Main = styled.main`
  background-color: #f8f9fa;
  width: 100%;
  height: 100vh;
  min-width: 1280px;
  padding-left: 0;
  box-sizing: border-box;
  
  @media (min-width: 768px) {
    padding-left: 195px;
  }
`;


const TopWrapper = styled.div`
  padding-left: 20px;
  border-bottom: 1px solid #ededed;
  height: 10vh;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  
  @media (min-width: 768px) {
    padding-left: 40px;
  }
  
  @media (min-width: 1024px) {
    padding-left: 80px;
  }
`;

const TopContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding: 0 4vw 0 0;
  box-sizing: border-box;

    @media (min-width: 768px) {
    padding-left: 40px;
    padding-right: 40px;
  }
  
  @media (min-width: 1024px) {
    padding-left: 0px;
    padding-right: 80px;
  }
`;

const TopTitle = styled.span`
  color: #2282e9;
  font-size: 20px;
  font-weight: 700;
`;

const BtnContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 10px;
  
  @media (min-width: 768px) {
    margin-top: 0;
  }
`;

const TopBtn = styled.button<{ isAvailable: boolean }>`
  width: 160px;
  height: 55px;
  border-radius: 5px;
  background-color: ${({ isAvailable }) => isAvailable ? '#2282E9' : '#E7E7E7'};
  border: none;
  color: white;
  font-weight: 600;
  cursor: ${({ isAvailable }) => isAvailable ? 'pointer' : 'not-allowed'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ isAvailable }) => isAvailable ? '#1B6FC5' : '#E7E7E7'};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 30px 20px 0;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  @media (min-width: 768px) {
    padding-left: 40px;
    padding-right: 40px;
  }
  
  @media (min-width: 1024px) {
    padding-left: 80px;
    padding-right: 80px;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  min-height: calc(20vh + 50px); // 기본: 화면 높이의 20% + 기본 패딩
height: auto;

  background-color: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px 20px 0; // 상하 패딩 추가
  z-index: 10;
  box-sizing: border-box;
  flex-wrap: wrap;
  position: relative;
  
 @media (min-width: 768px) {
    padding-right: 40px;
    min-height: calc(15vh + 30px); // 태블릿: 화면 높이의 15% + 패딩
  }
  
  @media (min-width: 1024px) {
    min-height: calc(12vh + 20px); // 데스크톱: 화면 높이의 12% + 패딩
  }
  
  @media (min-width: 1440px) {
    min-height: calc(10vh + 20px); // 대형 화면: 화면 높이의 10% + 패딩
  }
`;

const ContentNumber = styled.span`
  font-weight: 700;
  font-size: 32px;
  margin-left: 20px;
  color: #ededed;
  padding-top: 4px;
  
  @media (min-width: 768px) {
    font-size: 50px;
    margin-left: 32px;
  }
  
  @media (min-width: 1024px) {
    font-size: 60px;
    margin-left: 40px;
  }
`;

const ContentTextBox = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 20px;
  gap: 8px;
  
  @media (min-width: 768px) {
    margin-left: 47px;
  }
`;

const ContentTitle = styled.span`
  display: block;
  font-size: 18px;
  font-weight: 500;
`;

const ContentSubTitle = styled.span`
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: #9f9f9f;
  
  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const ContentLine = styled.div`
  height: 544px;
  width: 4px;
  position: absolute;
  left: 20%;
  transform: translateX(-50%);
  border-right: 2px dashed #d1d1d1;
  z-index: 5;
  display: none;
  
    display: block;
    left: auto;
    right: 10vw;
    // transform: 


`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TestDraft = () => {
  const { gameProposalId } = useParams();
  const { data: gameProposalDetailsData } = useQuery<GameProposalDetailsResponse>({
    queryKey: ["GameProposalOverview", gameProposalId],
    queryFn: async () => {
      const response = await GameProposalDetailsAPI(Number(gameProposalId));
      return response.data;
    },
    // initialData: {
    //   "gameProposalId": 37,
    //   "status": "APPROVED",
    //   "testPlanStatus": "APPROVED_FINAL",
    //   "projectInfoStatus": "APPROVED_FINAL",
    //   "questionCompositionStatus": "APPROVED_FINAL",
    //   "previewAvailable": true,
    //   "submitAvailable": true
    // },
    enabled: true
  });

  const isAllStageApproved = () => {
    return gameProposalDetailsData?.status === "APPROVED";
  };

  // 모달 상태 관리
  const [isTestStartModalOpen, setIsTestStartModalOpen] = useState(false);
  const navigate = useNavigate();

  const [testStartData, setTestStartData] = useState<{
    title: string;
    desiredTesterCount: number;
    totalPlimoney: number;
    userPlimoney: number;
    schedule: {
      recruitStartedAt: string;
      recruitExpiredAt: string;
      startedAt: string;
      expiredAt: string;
    }
  } | null>(null);


  const {
    mutate: fetchTestStartInfo,
    isPending: isTestDataLoading
  } = useMutation({
    mutationFn: async () => {
      const response = await LabsGameStartTestAPI(Number(gameProposalId));
      return response.data;
    },
    onSuccess: (data) => {
      setTestStartData(data);
      setIsTestStartModalOpen(true);
    },
    // enabled: false,
    onError: () => {

      const mockData = {
        "title": "GAME_TITLE",
        "desiredTesterCount": 50,
        "totalPlimoney": 7000,
        "userPlimoney": 10000,
        "schedule": {
          "recruitStartedAt": "2025-03-14T17:15:12.985275",
          "recruitExpiredAt": "2025-03-16T23:59:59",
          "startedAt": "2025-03-17T00:00:00",
          "expiredAt": "2025-03-18T23:59:59"
        }
      };
      setTestStartData(mockData);
      setIsTestStartModalOpen(true);

    }
  });


  const gameProposalStartTestMutation = useMutation({
    mutationFn: async () => {
      const response = await LabsGameProposalStartTestAPI(Number(gameProposalId));
      return response.data;
    },
    onSuccess: (data) => {
      console.log('테스트 시작하기 성공!');
      setIsTestStartModalOpen(false);
      navigate(`/home/test-list`);
    },
    onError: () => {
      setIsTestStartModalOpen(false);
      navigate(`/home/test-list`);
      alert("테스트 시작 요청에 실패했습니다.");
    }
  })

  // 테스트 시작 확인 모달에서 확인 버튼 클릭 시 실행할 함수
  const handleTestStart = async () => {
    
    gameProposalStartTestMutation.mutate();
    // window.location.reload();
  };

  const isButtonClickable = () => {
    if (gameProposalDetailsData?.status === "APPROVED") {
      return true;
    } else {
      if (gameProposalDetailsData?.submitAvailable) {
        return true;
      } else {
        return false;
      }
    }
  }

  return (
    <Main>
      <>
        <TopWrapper>
          <TopContainer>
            <TopTitle>게임 테스트 등록</TopTitle>
            <BtnContainer>
              <TopBtn
                isAvailable={isButtonClickable()}
                // disabled={!gameProposalDetailsData?.submitAvailable  }
                // disabled={!isButtonClickable()}
                onClick={() => {
                  if (gameProposalDetailsData?.status === "APPROVED") {
                    // useMutation 실행하여 API 호출
                    console.log("테스트 시작하기 버튼 클릭");

                    fetchTestStartInfo();
                  } else {
                    if (gameProposalDetailsData?.submitAvailable) {
                      navigate(`/home/test-list/${gameProposalId}/add/forth`);
                      console.log("테스트 등록 버튼 클릭 - TOODO 제출 로직 구현");
                    }

                    // 제출 로직 구현
                  }


                  if (gameProposalDetailsData?.submitAvailable) {
                    if (gameProposalDetailsData?.status === "APPROVED") {
                      console.log("테스트 시작하기 버튼 클릭");
                      // useMutation 실행하여 API 호출
                      fetchTestStartInfo();
                    } else {
                      // 제출 로직 구현
                      console.log("테스트 등록 버튼 클릭 - TOODO 제출 로직 구현");
                    }
                  }
                }}
              >
                {
                (isAllStageApproved() ? '테스트 시작하기' : 
                (gameProposalDetailsData?.status === 'REJECTED' ? '수정 완료 제출' : '테스트 등록 제출'))
                }
              </TopBtn>
            </BtnContainer>
          </TopContainer>
        </TopWrapper>
        <ContentWrapper>
          <ContentLine></ContentLine>
          <ContentContainer>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <StatusBox status={gameProposalDetailsData?.testPlanStatus} />
              <ContentNumber>1</ContentNumber>
              <ContentTextBox>
                <TitleWrapper>
                  <ContentTitle>테스트 플랜</ContentTitle>
                  <ApprovedCheck status={gameProposalDetailsData?.testPlanStatus} />
                </TitleWrapper>
                <ContentSubTitle>
                  진행하고자 하는 프로젝트와 맞는 플랜을 선택해 성공적인 FGT를
                  진행하세요.
                </ContentSubTitle>
              </ContentTextBox>
            </div>
            <StatusButton
              status={gameProposalDetailsData?.testPlanStatus}
              to={`/home/test-list/${gameProposalId}/add/first`}
            />

          </ContentContainer>
          <ContentContainer>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <StatusBox status={gameProposalDetailsData?.projectInfoStatus} />
              <ContentNumber>2</ContentNumber>
              <ContentTextBox>
                <TitleWrapper>
                  <ContentTitle>프로젝트 정보</ContentTitle>
                  <ApprovedCheck status={gameProposalDetailsData?.projectInfoStatus} />
                </TitleWrapper>
                <ContentSubTitle>
                  유저들이 프로젝트를 잘 이해할 수 있도록 게임에 관한 정보들을
                  작성해주세요.
                </ContentSubTitle>
              </ContentTextBox>
            </div>
            <StatusButton
              status={gameProposalDetailsData?.projectInfoStatus}
              to={`/home/test-list/${gameProposalId}/add/second`}
            />

          </ContentContainer>
          <ContentContainer>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <StatusBox status={gameProposalDetailsData?.questionCompositionStatus} />
              <ContentNumber>3</ContentNumber>
              <ContentTextBox>
                <TitleWrapper>
                  <ContentTitle>질문 구성</ContentTitle>
                  <ApprovedCheck status={gameProposalDetailsData?.questionCompositionStatus} />
                </TitleWrapper>
                <ContentSubTitle>
                  테스트를 구성할 질문들을 조합해 작성해주세요.
                </ContentSubTitle>
              </ContentTextBox>
            </div>
            <StatusButton
              status={gameProposalDetailsData?.questionCompositionStatus}
              to={`/home/test-list/${gameProposalId}/add/third`}
            />
          </ContentContainer>
          <ContentContainer>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <StatusBox status={String(gameProposalDetailsData?.previewAvailable)} />
              <ContentNumber>4</ContentNumber>
              <ContentTextBox>
                <TitleWrapper>
                  <ContentTitle>견적 확인</ContentTitle>
                  {isAllStageApproved() && <ApprovedCheck status="COMPLETE" />}
                </TitleWrapper>
                <ContentSubTitle>
                  1, 2, 3단계 프로젝트 내용을 모두 작성 후 견적을
                  확인해주세요.
                </ContentSubTitle>
              </ContentTextBox>
            </div>
            <FinalStatusButton to={`/home/test-list/${gameProposalId}/add/forth`}
              isAvailable={gameProposalDetailsData?.previewAvailable} />

          </ContentContainer>
        </ContentWrapper>
      </>

      {/* 테스트 시작 확인 모달 */}
      {testStartData && (
        <CheckTestStartModal
          isOpen={isTestStartModalOpen}
          onClose={() => setIsTestStartModalOpen(false)}
          onConfirm={handleTestStart}
          data={testStartData}
        />
      )}
    </Main>
  )
}

export default TestDraft;